// FlightCompletionListener.jsx
// Watches for flight completions and triggers toast notifications
// Renders nothing - just listens and triggers toasts

import { useEffect, useRef } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { useFlightToast } from "../ui/FlightToast.jsx";
import { debug } from '../../lib/debug.js';

export default function FlightCompletionListener() {
  const { worldState } = useWorldState();
  const { showFlightComplete } = useFlightToast();

  // Track completed flights we've already toasted (or were already completed on load)
  const toastedFlightsRef = useRef(new Set());
  // Has first render happened?
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const flights = worldState?.flights || [];

    // FIRST RENDER: Seed the Set with all currently completed flights
    // These were completed BEFORE we loaded - don't toast them
    // IMPORTANT: Only initialize once we actually have flights to check
    if (!hasInitializedRef.current) {
      if (flights.length === 0) {
        // No flights loaded yet - wait for next render
        return;
      }
      hasInitializedRef.current = true;
      flights.forEach((flight) => {
        if (flight && (flight.phase === "completed" || flight.status === "completed")) {
          toastedFlightsRef.current.add(flight.id);
        }
      });
      debug("[FlightCompletionListener] Seeded with", toastedFlightsRef.current.size, "completed flights");
      return; // Don't process anything else on first render
    }

    // SUBSEQUENT RENDERS: Check for newly completed flights
    flights.forEach((flight) => {
      if (!flight) return;

      const isCompleted = flight.phase === "completed" || flight.status === "completed";

      // Skip if already toasted
      if (toastedFlightsRef.current.has(flight.id)) return;

      if (isCompleted) {
        // This flight just completed during gameplay - show toast!
        showFlightComplete({
          origin: flight.origin,
          destination: flight.destination,
          revenue: flight.revenue || flight.estimatedRevenue || flight.preCalculatedEarnings?.revenue || 0,
          passengers: flight.passengers || flight.passengerCount || flight.pax || 0,
          totalDelayMinutes: flight.totalDelayMinutes || flight.delayMinutes || 0,
        });
        debug("[FlightCompletionListener] Toast shown for flight:", flight.id);

        // Mark as toasted
        toastedFlightsRef.current.add(flight.id);
      }
    });
  }, [worldState?.flights, showFlightComplete]);

  // This component renders nothing
  return null;
}
